import { FC, useEffect, useRef, useState } from "react";
import { FaTrashAlt, FaPlus } from "react-icons/fa";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";

export interface Image {
  path: string;
  public_id: string;
  primaryImg: boolean;
  _id: string;
}

interface ImageUploadProps {
  onUpload: (image: File | Image | null) => void; // Accept both File and Image types
}

const ImageUpload: FC<ImageUploadProps> = ({ onUpload }) => {
  const { brandDetails } = useSelector(
    (state: any) => state.rootReducer?.brandDetail
  );

  const [selectedImage, setSelectedImage] = useState<File | Image | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const params = useParams();

  useEffect(() => {
    // Load existing image from productDetails if available
    if (brandDetails?.logo) {
      setSelectedImage(brandDetails.logo); // Assuming only one image is needed
    }
  }, [brandDetails]);

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files && files[0]) {
      const file = files[0];
      const preview = URL.createObjectURL(file);

      // Replace the currently selected image with the new file
      setSelectedImage(file); // Set state as File type
      onUpload(file); // Pass the new file to the parent
    }
  };

  const handleRemoveImage = () => {
    setSelectedImage(null);
    onUpload(null); // Notify parent about the removal
  };

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        JPEG / PNG, Max Size - 5MB
      </label>
      <div className="flex items-center space-x-2 text-sm text-gray-500 mb-4">
        <span>{selectedImage ? "1 Image Selected" : "No Image Selected"}</span>
      </div>
      <div className="grid grid-cols-8 gap-4">
        {/* Render selected image */}
        {selectedImage && (
          <div className="relative w-32 h-32">
            <img
              src={
                "path" in selectedImage
                  ? selectedImage.path // For existing image
                  : URL.createObjectURL(selectedImage as File) // For new file
              }
              alt="Selected"
              className="w-full h-full object-cover rounded-md"
            />
            <button
              onClick={handleRemoveImage}
              className="absolute top-1 right-1 bg-primary1 text-white rounded-full p-2 hover:bg-primary3 shadow-md"
            >
              <FaTrashAlt />
            </button>
          </div>
        )}

        {/* Add new image button */}
        {!selectedImage && (
          <div
            onClick={handleUploadClick}
            className="w-32 h-32 flex items-center justify-center border border-dashed border-gray-300 rounded-md cursor-pointer hover:border-primary3 transition-all"
          >
            <FaPlus className="text-primary1 text-3xl" />
          </div>
        )}
      </div>

      <input
        type="file"
        accept="image/*"
        ref={fileInputRef}
        onChange={handleFileChange}
        className="hidden"
      />
    </div>
  );
};

export default ImageUpload;
